'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, ChevronRight, Loader2, MapPin, Play, Search, X } from 'lucide-react';
import { useLocale, useFormatter, useTranslations } from 'next-intl';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Input } from '@/components/ui/input/input';
import { Flag } from '@/components/ui/flag/flag';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';
import { Button } from '@/components/ui/button';
import { type Branding } from '@/lib/branding';
import { buildLocalePath, type Locale } from '@/i18n/config';
import type { MapboxMap } from '@/types/mapbox';
import { RideDayCTACards } from './live/components/RideDayCTACards';

const YOUTUBE_ID = 'x0xdIb3Ay7M';

function YouTubeEmbed({ videoId }: { videoId: string }) {
  const [playing, setPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (playing || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPlaying(true);
          observer.disconnect();
        }
      },
      { threshold: 0.6 },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [playing]);

  return (
    <div ref={containerRef} className="aspect-video w-full">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&rel=0&modestbranding=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
          title="The Distinguished Gentleman's Drive video"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group relative h-full w-full cursor-pointer overflow-hidden bg-black"
        >
          <img
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt="Play video"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-dgd-gold transition-transform group-hover:scale-110 sm:h-20 sm:w-20">
              <Play className="h-7 w-7 fill-black text-black sm:h-8 sm:w-8" />
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

gsap.registerPlugin(ScrollTrigger);

const HERO_IMAGE_URL = '/images/dgd/DGD26_WEBHEADER_FINAL.jpg';

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

type RideStatus = 'open' | 'almost_full' | 'at_capacity';

interface GlobalRideRow {
  id: string;
  city: string;
  slug: string;
  stateName: string | null;
  stateCode: string | null;
  countryName: string;
  countryCode: string;
  eventDate: string | null;
  startTime: string | null;
  capacity: string | number | null;
  currentRegistrations: number;
  raised: number;
  donationCount: number;
  currencyCode: string;
  coordinates: string | null;
}

interface CountryEventStat {
  countryCode: string;
  countryName: string;
  ridesCount: number;
  entrantsCount: number;
  totalRaised: number;
  donationCount: number;
  currencyCode: string;
}

interface GlobalRide extends GlobalRideRow {
  countrySlug: string;
  status: RideStatus;
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseCapacity(value: string | number | null): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toRideStatus(currentRegistrations: number, capacity: string | number | null): RideStatus {
  const max = parseCapacity(capacity);
  if (!max) return 'open';
  if (currentRegistrations >= max) return 'at_capacity';
  if (currentRegistrations >= max * 0.85) return 'almost_full';
  return 'open';
}

export interface LandingPageProps {
  branding?: Branding;
}

export function LandingPage({ branding }: LandingPageProps) {
  const locale = useLocale() as Locale;
  // tCountdown still pulls from the legacy DGR-shared `hero.*` namespace for
  // countdown labels + section subtitle. tHero pulls DGD-specific copy.
  const tCountdown = useTranslations('hero');
  const tHero = useTranslations('dgdHero');
  const tAbout = useTranslations('dgdAbout');
  const tCta = useTranslations('dgdCta');
  const tNav = useTranslations('nav');
  const tEvents = useTranslations('events');
  const format = useFormatter();
  const { currency: regionalCurrency } = useRegional();

  const registerHref = buildLocalePath(locale, '/register');
  const donateHref = buildLocalePath(locale, '/donate');

  const heroRef = useRef<HTMLElement>(null);
  const EVENT_DATE = new Date('2026-09-27T00:00:00');
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, EVENT_DATE.getTime() - now.getTime());
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const videoRef = useRef<HTMLDivElement>(null);
  const ctaSectionRef = useRef<HTMLElement>(null);
  const driveDaySectionRef = useRef<HTMLElement>(null);

  // Scroll to video section when ?wrapupvideo is present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('wrapupvideo') && videoRef.current) {
      const timer = setTimeout(() => {
        videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Globe map state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const globeMap = useRef<MapboxMap | null>(null);
  const [globeMapLoaded, setGlobeMapLoaded] = useState(false);
  const [globeMapInitialized, setGlobeMapInitialized] = useState(false);

  const { data: countryStats = [] } = trpc.publicLookups.countryEventStats.useQuery({
    currencyCode: regionalCurrency,
  });
  const { data: globalRidesRaw = [] } = trpc.publicLookups.globalMapRides.useQuery({
    currencyCode: regionalCurrency,
  });

  const globalRides = useMemo<GlobalRide[]>(
    () =>
      (globalRidesRaw as GlobalRideRow[]).map((ride) => ({
        ...ride,
        countrySlug: toSlug(ride.countryName),
        status: toRideStatus(ride.currentRegistrations, ride.capacity),
      })),
    [globalRidesRaw],
  );

  const sortedCountries = useMemo(
    () =>
      [...(countryStats as CountryEventStat[])]
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((c) => ({
          ...c,
          slug: toSlug(c.countryName),
        })),
    [countryStats],
  );

  const filteredRides = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return globalRides
      .filter(
        (ride) =>
          ride.city.toLowerCase().includes(query) ||
          ride.countryName.toLowerCase().includes(query) ||
          (ride.stateName ?? '').toLowerCase().includes(query),
      )
      .slice(0, 8);
  }, [searchQuery, globalRides]);

  const parseCoordinates = useCallback(
    (coords: string | null): { lat: number; lng: number } | null => {
      if (!coords) return null;
      const parts = coords.split(',').map((s) => s.trim());
      if (parts.length !== 2) return null;
      const [lat, lng] = parts.map(Number);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    },
    [],
  );

  // Load Mapbox GL JS
  useEffect(() => {
    if (!MAPBOX_TOKEN) return;

    const existingStyle = document.querySelector('style[data-mapbox-custom]');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.setAttribute('data-mapbox-custom', 'true');
      style.textContent = `
        .mapboxgl-popup { z-index: 10; }
        .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
          overflow: visible;
        }
        .mapboxgl-popup-close-button {
          position: absolute; top: -25px; right: -25px;
          width: 24px; height: 24px; font-size: 18px; line-height: 22px;
          text-align: center; color: #333; background: #1c1917;
          border-radius: 50%; padding: 10px 20px; border: 2px solid white;
          cursor: pointer; transition: all 0.15s ease; z-index: 2;
        }
        .mapboxgl-popup-close-button:hover {
          transform: scale(1.1);
          background-color: transparent !important;
        }
        .mapboxgl-popup-tip { border-top-color: white !important; }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib { display: none !important; }
      `;
      document.head.appendChild(style);
    }

    if (window.mapboxgl) {
      queueMicrotask(() => setGlobeMapLoaded(true));
      return;
    }

    const existingLink = document.querySelector('link[href*="mapbox-gl"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[src*="mapbox-gl"]');
    if (existingScript) {
      if (window.mapboxgl) {
        queueMicrotask(() => setGlobeMapLoaded(true));
      } else {
        existingScript.addEventListener('load', () => setGlobeMapLoaded(true));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => setGlobeMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize globe map
  useEffect(() => {
    if (!globeMapLoaded || !mapContainer.current || !MAPBOX_TOKEN) return;
    if (globeMap.current) return;

    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const isMd = window.innerWidth >= 768;
    globeMap.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [10, 30],
      zoom: isMd ? 2.2 : 1.6,
      minZoom: isMd ? 1.5 : 1,
      maxZoom: 18,
      projection: 'globe',
    });

    globeMap.current.on('load', () => {
      const m = globeMap.current as unknown as Record<string, (...args: unknown[]) => void>;
      // Cream-tinted oceans
      m.setPaintProperty('water', 'fill-color', '#fbf1ef');
      // Subtle globe atmosphere
      m.setFog({
        color: '#fbf1ef',
        'high-color': '#fbf1ef',
        'horizon-blend': 0.02,
        'space-color': '#fdf8f7',
        'star-intensity': 0,
      });
      setGlobeMapInitialized(true);
    });

    return () => {
      if (globeMap.current) {
        globeMap.current.remove();
        globeMap.current = null;
      }
      setGlobeMapInitialized(false);
    };
  }, [globeMapLoaded]);

  // Add clustered GeoJSON source + layers
  useEffect(() => {
    if (!globeMapInitialized || !globeMap.current) return;
    const m = globeMap.current;
    const mapboxgl = window.mapboxgl;

    // Remove old source/layers if they exist
    if (m.getLayer('cluster-count')) m.removeLayer('cluster-count');
    if (m.getLayer('clusters')) m.removeLayer('clusters');
    if (m.getLayer('unclustered-point')) m.removeLayer('unclustered-point');
    if (m.getSource('rides')) m.removeSource('rides');

    const features = globalRides
      .map((ride) => {
        const coords = parseCoordinates(ride.coordinates);
        if (!coords) return null;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [coords.lng, coords.lat] },
          properties: {
            id: ride.id,
            city: ride.city,
            countryName: ride.countryName,
            countryCode: ride.countryCode,
            countrySlug: ride.countrySlug,
            slug: ride.slug,
            stateName: ride.stateName ?? '',
            riders: ride.currentRegistrations,
            raised: ride.raised,
            status: ride.status,
          },
        };
      })
      .filter(Boolean);

    m.addSource('rides', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      } as unknown as import('@/types/mapbox').GeoJSONData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    // Cluster circles — gold shades based on point_count
    m.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'rides',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#c9a227', 10, '#a5842e', 30, '#7a6222'],
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 28, 30, 36],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Cluster count labels
    m.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'rides',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13,
      } as Record<string, unknown>,
      paint: {
        'text-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Unclustered individual points
    m.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'rides',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#c9a227',
        'circle-radius': 7,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Click cluster → zoom in
    m.on('click', 'clusters', (e: import('@/types/mapbox').MapboxMapEvent) => {
      const features = m.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id as number;
      const source = m.getSource('rides');
      if (source) {
        source.getClusterExpansionZoom(clusterId, (_err: unknown, zoom: number) => {
          m.flyTo({
            center: features[0].geometry.coordinates as unknown as [number, number],
            zoom,
          });
        });
      }
    });

    // Click unclustered point → show popup
    m.on('click', 'unclustered-point', (e: import('@/types/mapbox').MapboxMapEvent) => {
      const feat = m.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
      if (!feat.length) return;
      const props = feat[0].properties as Record<string, unknown>;
      const coords = feat[0].geometry.coordinates as unknown as [number, number];

      const formattedRaised = format.number(props.raised as number, {
        style: 'currency',
        currency: regionalCurrency,
        notation: 'compact',
        maximumFractionDigits: 0,
      });

      new mapboxgl.Popup({ offset: 15, closeButton: true })
        .setLngLat(coords)
        .setHTML(
          `<div style="padding: 14px; min-width: 220px; font-family: system-ui, sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <img src="/flags/${(props.countryCode as string).toUpperCase()}.svg" alt="${props.countryName}" style="width: 18px; height: 13px; border-radius: 2px; object-fit: cover;" />
              <span style="font-size: 11px; color: #78716c;">${props.countryName}</span>
            </div>
            <h3 style="font-weight: 600; margin: 0 0 4px; font-size: 16px; color: #1c1917;">${props.city}</h3>
            ${props.stateName ? `<p style="margin: 0 0 10px; font-size: 12px; color: #78716c;">${props.stateName}</p>` : ''}
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; margin-bottom: 12px; font-size: 11px;">
              <div>
                <p style="margin: 0; color: #78716c;">${tEvents('drivers')}</p>
                <p style="margin: 0; font-weight: 600; color: #1c1917;">${format.number(props.riders as number)}</p>
              </div>
              <div>
                <p style="margin: 0; color: #78716c;">${tEvents('raised')}</p>
                <p style="margin: 0; font-weight: 600; color: hsl(45 67% 38%);">${formattedRaised}</p>
              </div>
            </div>
            <a href="/events/${props.countrySlug}/${props.slug}" style="display: block; text-align: center; padding: 8px 14px; background: #1c1917; color: white; border-radius: 8px; font-size: 12px; font-weight: 500; text-decoration: none;">${tEvents('viewEvent')}</a>
          </div>`,
        )
        .addTo(m);
    });

    // Pointer cursor on interactive layers
    m.on('mouseenter', 'clusters', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'clusters', () => {
      m.getCanvas().style.cursor = '';
    });
    m.on('mouseenter', 'unclustered-point', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'unclustered-point', () => {
      m.getCanvas().style.cursor = '';
    });
  }, [globeMapInitialized, parseCoordinates, globalRides, format, tEvents, regionalCurrency]);

  const flyToRide = useCallback(
    (ride: GlobalRide) => {
      if (!globeMap.current) return;
      const coords = parseCoordinates(ride.coordinates);
      if (!coords) return;
      setSearchQuery('');
      setSearchFocused(false);

      globeMap.current.flyTo({
        center: [coords.lng, coords.lat],
        zoom: 12,
        duration: 2000,
      });
    },
    [parseCoordinates],
  );

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !videoRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        videoRef.current,
        { width: '65%', borderRadius: '1rem' },
        {
          width: '92%',
          borderRadius: '1.5rem',
          ease: 'power2.out',
          scrollTrigger: {
            trigger: videoRef.current,
            start: 'top 90%',
            end: 'top 20%',
            scrub: 0.4,
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !ctaSectionRef.current) return;

    const children = ctaSectionRef.current.querySelectorAll('[data-reveal]');
    if (!children.length) return;

    const ctx = gsap.context(() => {
      gsap.set(children, { y: 40, opacity: 0 });

      gsap.to(children, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.15,
        scrollTrigger: {
          trigger: ctaSectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !driveDaySectionRef.current) return;

    const children = driveDaySectionRef.current.querySelectorAll('[data-driveday-reveal]');
    if (!children.length) return;

    const ctx = gsap.context(() => {
      gsap.set(children, { y: 40, opacity: 0 });

      gsap.to(children, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.12,
        scrollTrigger: {
          trigger: driveDaySectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      {/* Hero Section — DGD26 art-deco illustration on flat navy */}
      <section
        ref={heroRef}
        className="relative z-10 h-[78vh] min-h-[520px] md:h-[90vh] md:min-h-[600px] flex flex-col overflow-visible bg-dgd-navy"
      >
        {/* Background artwork — flat navy on the left, illustration on the right.
            Mobile framing keeps the couple and the roadster in view. */}
        <div className="absolute inset-0 z-0">
          <Image
            src={HERO_IMAGE_URL}
            alt="Classic cars and a dapper couple driving into the sunset"
            fill
            className="object-cover object-[78%_center] md:object-right"
            priority
          />
          {/* Navy wash keeps text legible when the artwork slides under it on
              smaller screens; invisible over the flat navy left on desktop */}
          <div className="absolute inset-0 bg-gradient-to-r from-dgd-navy/95 via-dgd-navy/60 to-dgd-navy/20 md:from-dgd-navy/40 md:via-transparent md:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-dgd-navy/70 via-dgd-navy/40 to-dgd-navy/10 md:hidden" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center pt-20">
          <div className="container">
            <div className="max-w-2xl">
              {/* Event Date */}
              <p className="font-dgd-display text-base sm:text-lg tracking-[0.06em] text-dgd-gold-light mb-6">
                {tHero('eventDate')}
              </p>

              {/* Main Heading */}
              <h1 className="font-dgd-display uppercase tracking-tighter text-[2.5rem] sm:text-6xl lg:text-7xl text-dgd-cream leading-[1.05]">
                {tHero('headline')}
                <br />
                <span className="text-dgd-gold">{tHero('headlineAccent')}</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg text-dgd-cream/85 leading-relaxed">
                {tHero('subhead')}
              </p>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-row gap-4">
                <Button
                  size="lg"
                  asChild
                  className="bg-dgd-gold text-dgd-charcoal hover:bg-dgd-gold-hover text-base px-8 w-1/2 sm:w-1/3"
                >
                  <Link href={registerHref} prefetch={false}>
                    {tNav('register')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  asChild
                  className="border border-dgd-gold/60 bg-transparent text-dgd-cream hover:border-dgd-gold hover:bg-dgd-cream/10 text-base px-8 w-1/2 sm:w-1/3"
                >
                  <Link href={donateHref} prefetch={false}>
                    {tNav('donate')}
                  </Link>
                </Button>
              </div>

              {/* Charity beneficiary */}
              <div className="mt-12 flex flex-col gap-2">
                <span className="text-dgd-cream/60 text-xs uppercase tracking-widest">
                  {tHero('proudlySupporting')}
                </span>
                <Link
                  href="https://movember.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-fit"
                >
                  <Image
                    src="/images/dgr/sponsors/MovemberLogo.svg"
                    alt="Movember"
                    width={160}
                    height={16}
                    className="h-5 w-auto"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-dgd-cream text-dgd-charcoal py-20 sm:py-28">
        <div className="container max-w-5xl">
          <div className="text-center">
            <h2 className="font-dgd-display text-4xl sm:text-5xl tracking-tight text-dgd-navy">
              {tAbout('title')}
            </h2>
            <p className="mt-6 mx-auto max-w-3xl text-lg leading-relaxed text-dgd-charcoal/85">
              {tAbout('body')}
            </p>
            <div className="mt-8 inline-flex items-center px-5 py-2 rounded-full border border-dgd-tan/60 text-dgd-tan text-sm font-medium">
              {tAbout('eligibility')}
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', label: tAbout('tagline1') },
              { num: '02', label: tAbout('tagline2') },
              { num: '03', label: tAbout('tagline3') },
            ].map((item) => (
              <div
                key={item.num}
                className="flex flex-col items-center text-center border-t border-dgd-tan/30 pt-6"
              >
                <span aria-hidden="true" className="font-dgd-display text-3xl text-dgd-tan">
                  {item.num}
                </span>
                <span className="mt-3 font-dgd-display text-2xl text-dgd-charcoal">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-16 text-center text-sm text-dgd-charcoal/60 max-w-2xl mx-auto">
            {tAbout('cause')}
          </p>
        </div>
      </section>

      {/* Countdown + CTA + Video Section */}
      <section ref={ctaSectionRef} className="bg-dgd-navy text-dgd-cream py-20 sm:py-28">
        <div className="container flex flex-col items-center text-center py-8 md:py-20">
          <p data-reveal className="text-dgd-gold text-lg uppercase font-bold tracking-widest">
            {tHero('eventDate')}
          </p>
          <p data-reveal className="mt-4 max-w-lg text-lg text-dgd-cream/75">
            {tHero('subhead')}
          </p>

          {/* Countdown */}
          <div data-reveal className="mt-10 flex gap-4 sm:gap-6">
            {[
              { value: countdown.days, label: tCountdown('countdownDays') },
              { value: countdown.hours, label: tCountdown('countdownHours') },
              { value: countdown.minutes, label: tCountdown('countdownMinutes') },
              { value: countdown.seconds, label: tCountdown('countdownSeconds') },
            ].map((unit) => (
              <div key={unit.label} className="flex flex-col items-center">
                <div className="flex h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 items-center justify-center rounded-xl border border-dgd-gold/25 bg-dgd-cream/5">
                  <span className="font-dgd-display text-5xl sm:text-6xl text-dgd-cream tabular-nums">
                    {String(unit.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="mt-2 text-xs uppercase tracking-widest text-dgd-cream/60">
                  {unit.label}
                </span>
              </div>
            ))}
          </div>

          {/* Register CTA */}
          <div data-reveal>
            <Button
              size="lg"
              asChild
              className="mt-10 bg-dgd-gold text-dgd-charcoal hover:bg-dgd-gold-hover text-base px-10"
            >
              <Link href={registerHref} prefetch={false}>
                {tNav('register')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Video */}
          <div data-reveal ref={videoRef} className="mt-16" style={{ width: '65%' }}>
            <div className="overflow-hidden rounded-2xl bg-stone-900 shadow-2xl shadow-black/40">
              <YouTubeEmbed videoId={YOUTUBE_ID} />
            </div>
          </div>
        </div>
      </section>

      {/* Drive Day 2026 — on-the-day CTAs (mirrors /live) */}
      <section
        ref={driveDaySectionRef}
        className="relative overflow-hidden bg-dgd-navy text-dgd-cream py-20 sm:py-28"
      >
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-dgd-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-dgd-gold/5 blur-3xl" />
        </div>

        <div className="container relative">
          <div className="mx-auto max-w-3xl text-center">
            <p
              data-driveday-reveal
              className="text-xs uppercase tracking-[0.3em] font-medium text-dgd-gold"
            >
              {tHero('eventDate')}
            </p>
            <h2
              data-driveday-reveal
              className="mt-4 font-dgd-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-dgd-cream"
            >
              Drive Day <span className="text-dgd-gold">2026</span>
            </h2>
            <p
              data-driveday-reveal
              className="mt-5 text-base md:text-lg leading-relaxed text-dgd-cream/75"
            >
              One day. One drive. Twenty-four hours of distinguished gentlefolk circling the globe —
              together.
            </p>
            <div data-driveday-reveal className="mt-8">
              <Button
                size="lg"
                asChild
                className="border border-dgd-gold/60 bg-transparent text-dgd-cream hover:border-dgd-gold hover:bg-dgd-cream/10 text-base px-8"
              >
                <Link href={buildLocalePath(locale, '/live')} prefetch={false}>
                  <span className="relative mr-2.5 flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  Track the drive live
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <RideDayCTACards
            locale={locale}
            tone="dark"
            revealAttr="data-driveday-reveal"
            showHeading={false}
            className="mt-14 md:mt-16"
          />
        </div>
      </section>

      <main className="flex-1">
        {/* Globe Map Section */}
        <section className="relative bg-dgd-cream text-dgd-charcoal py-20">
          <div className="container">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-dgd-tan mb-3">
                {tEvents('findYourDrive')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900">
                {tEvents('eventsWorldwide')}
              </h2>
            </div>

            {/* Country selector */}
            <div className="flex flex-col items-center gap-3 mb-8">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                  onBlur={() => setTimeout(() => setCountryDropdownOpen(false), 200)}
                  className="flex items-center justify-between w-72 md:w-80 h-11 px-4 bg-white border border-stone-200 rounded-xl text-sm text-stone-900 hover:border-dgd-gold transition-colors"
                >
                  <span className={selectedCountry ? 'text-stone-900' : 'text-stone-400'}>
                    {selectedCountry
                      ? (sortedCountries.find((c) => c.countryCode === selectedCountry)
                          ?.countryName ?? tEvents('selectCountry'))
                      : tEvents('selectCountry')}
                  </span>
                  <ChevronRight
                    className={`w-4 h-4 text-stone-400 transition-transform ${countryDropdownOpen ? 'rotate-90' : ''}`}
                  />
                </button>

                {countryDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-72 md:w-80 bg-white rounded-xl shadow-lg border border-stone-200 max-h-64 overflow-y-auto z-20">
                    {sortedCountries.map((country) => (
                      <button
                        key={country.countryCode}
                        onClick={() => {
                          setSelectedCountry(country.countryCode);
                          setCountryDropdownOpen(false);
                          if (!globeMap.current) return;
                          const countryRides = globalRides.filter(
                            (r) => r.countryCode === country.countryCode,
                          );
                          if (!countryRides.length) return;
                          const allCoords = countryRides
                            .map((r) => parseCoordinates(r.coordinates))
                            .filter(Boolean) as { lat: number; lng: number }[];
                          if (!allCoords.length) return;
                          if (allCoords.length === 1) {
                            globeMap.current.flyTo({
                              center: [allCoords[0].lng, allCoords[0].lat],
                              zoom: 6,
                              duration: 2000,
                            });
                          } else {
                            const bounds = new window.mapboxgl.LngLatBounds();
                            allCoords.forEach((c) => bounds.extend([c.lng, c.lat]));
                            globeMap.current.fitBounds(bounds.toArray(), {
                              padding: 80,
                              maxZoom: 10,
                            });
                          }
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 transition-colors text-left"
                      >
                        <Flag code={country.countryCode} size="sm" className="rounded" />
                        <span className="flex-1 text-sm font-medium text-stone-900">
                          {country.countryName}
                        </span>
                        <span className="text-xs text-stone-400">
                          {country.ridesCount} {tEvents('drives').toLowerCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedCountry && (
                <Link
                  href={`/events/${sortedCountries.find((c) => c.countryCode === selectedCountry)?.slug ?? ''}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-dgd-gold text-dgd-charcoal font-medium rounded-xl hover:bg-dgd-gold-hover transition-colors text-sm"
                >
                  {tEvents('viewAllEventsIn', {
                    country:
                      sortedCountries.find((c) => c.countryCode === selectedCountry)?.countryName ??
                      '',
                  })}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            <div className="relative mx-auto max-w-5xl">
              {/* Search bar */}
              <div className="absolute top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[420px] z-10">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    placeholder={tEvents('searchCities')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="h-11 pl-12 pr-12 bg-white border-stone-200 shadow-lg text-stone-900 placeholder:text-stone-400 text-sm rounded-xl"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {searchFocused && filteredRides.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden">
                      {filteredRides.map((ride) => (
                        <button
                          key={ride.id}
                          onClick={() => flyToRide(ride)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                        >
                          <Flag code={ride.countryCode} size="sm" className="rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900 text-sm">{ride.city}</p>
                            <p className="text-xs text-stone-500 truncate">
                              {ride.stateName ? `${ride.stateName}, ` : ''}
                              {ride.countryName}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchFocused && searchQuery && filteredRides.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 p-4 text-center">
                      <p className="text-stone-500 text-sm">{tEvents('noCitiesFound')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Globe map */}
              <div
                ref={mapContainer}
                className="h-[600px] md:h-[800px] w-full rounded-2xl overflow-hidden bg-[#fdf8f7]"
              />

              {!globeMapInitialized && MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#fdf8f7] rounded-2xl z-[5]">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('loadingGlobalMap')}</p>
                  </div>
                </div>
              )}

              {!MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#fdf8f7] rounded-2xl z-[5]">
                  <div className="text-center">
                    <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('mapNotAvailable')}</p>
                  </div>
                </div>
              )}

              {/* Status legend */}
              <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-md px-3 py-2.5 z-10">
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-600 border border-white shadow-sm" />
                    <span className="text-stone-500">{tEvents('statusAlmostFull')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-600 border border-white shadow-sm" />
                    <span className="text-stone-500">{tEvents('statusFull')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-dgd-navy text-dgd-cream py-20 sm:py-24">
          <div className="container max-w-3xl text-center">
            <h2 className="font-dgd-display text-4xl sm:text-5xl tracking-tight text-dgd-cream">
              {tCta('title')}
            </h2>
            <p className="mt-4 text-lg text-dgd-cream/85">{tCta('subtitle')}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-dgd-gold text-dgd-charcoal hover:bg-dgd-gold-hover text-base px-10"
              >
                <Link href={registerHref} prefetch={false}>
                  {tNav('register')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                asChild
                className="border border-dgd-gold/60 bg-transparent text-dgd-cream hover:border-dgd-gold hover:bg-dgd-cream/10 text-base px-10"
              >
                <Link href={donateHref} prefetch={false}>
                  {tNav('donate')}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
